<template>
  <div class="rv">
    <h1 class="rv-h1">
      My reviews
    </h1>
    <p class="rv-lead">
      After a date, couples can rate their location and pick tags. They tend to be <strong>more critical</strong>
      because we choose the location for them. Feedback is optional and never shared publicly.
    </p>

    <div class="rv-stack">
      <ds-card title="Filters">
        <div class="rv-filters">
          <div class="rv-filter">
            <label
              for="rv-from"
              class="rv-filter-label"
            >From</label>
            <b-form-datepicker
              id="rv-from"
              v-model="from"
              :value-as-date="true"
              :date-format-options="dateLabelFormat"
              @input="loadReviews"
            />
          </div>

          <div class="rv-filter">
            <label
              for="rv-until"
              class="rv-filter-label"
            >Until</label>
            <b-form-datepicker
              id="rv-until"
              v-model="until"
              :value-as-date="true"
              :date-format-options="dateLabelFormat"
              @input="loadReviews"
            />
          </div>

          <div class="rv-filter">
            <label
              for="rv-sort"
              class="rv-filter-label"
            >Sorting</label>
            <ds-select
              id="rv-sort"
              v-model="sortOrder"
              :options="sortOptions"
              @input="loadReviews"
            />
          </div>

          <div class="rv-filter wide">
            <span class="rv-filter-label">Filter on tags</span>
            <multiselect
              v-model="filteredTags"
              :options="possibleTags"
              :multiple="true"
              :show-labels="false"
              :close-on-select="false"
              :clear-on-select="false"
              label="label"
              track-by="value"
              placeholder="Pick some"
              @input="loadReviews"
            >
              <template
                v-slot:selection="{ values, search, isOpen }"
              ><span
                v-if="values.length"
                v-show="!isOpen"
                class="multiselect__single"
              >{{ values.length }} options selected</span>
              </template>
            </multiselect>
          </div>

          <div class="rv-filter switch">
            <span class="rv-filter-label">Only reviews with written text</span>
            <ds-switch
              :value="withReview"
              aria-label="Only reviews with written text"
              @change="onWithReviewChange"
            />
          </div>
        </div>
      </ds-card>

      <ds-card>
        <!-- Only the first load blanks the card. A refetch keeps the list on screen and
             dims it, so tweaking a filter can't collapse the card to a spinner and jump
             back to full height. -->
        <div
          v-if="loading"
          class="rv-loading"
          role="status"
        >
          <ds-spinner size="md" />
          <span>Loading your reviews…</span>
        </div>

        <template v-else>
          <p
            class="rv-summary"
            aria-live="polite"
          >
            <ds-spinner
              v-if="refetching"
              class="rv-summary-spinner"
            />
            <template v-if="reviews.length > 0">
              Showing first {{ Math.min(100, reviews.length) }} of {{ total }} reviews between
              {{ formatDate('yyyy-MM-dd', from) }} and {{ formatDate('yyyy-MM-dd', until) }}:
            </template>
            <template v-else>
              No reviews found between {{ formatDate('yyyy-MM-dd', from) }} and
              {{ formatDate('yyyy-MM-dd', until) }}
            </template>
          </p>

          <div
            v-if="reviews.length > 0"
            class="rv-list"
            :class="{ refetching }"
            :aria-busy="refetching ? 'true' : 'false'"
          >
            <review
              v-for="review in reviews"
              :key="review.feedbackId"
              :review="review"
              @reply="startReply"
              @exception="startException"
            />
          </div>
        </template>
      </ds-card>
    </div>

    <breeze-modal
      ref="replyModal"
      v-model="reply.open"
      title="Reply to review"
      ok-button="Send"
      @ok="sendReply"
    >
      <p class="rv-modal-text">
        Type your reply here and we’ll send that over to the user in the app.
      </p>
      <ds-textarea
        v-model="reply.message"
        placeholder="Type your reply here"
        rows="3"
        max-rows="6"
      />
    </breeze-modal>

    <breeze-modal
      ref="exceptionModal"
      v-model="exception.open"
      title="Let us know if you don’t agree with a review"
      ok-button="Send"
      @ok="sendException"
    >
      <p class="rv-modal-text">
        It could be that a negative review is not your fault, but, for example, ours. If so, you can let us know why
        you think this is the case. We’ll take a look and remove it.
      </p>
      <ds-textarea
        v-model="exception.message"
        placeholder="Reason"
        rows="3"
        max-rows="6"
      />
    </breeze-modal>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import {
  DsCard, DsSelect, DsSpinner, DsSwitch, DsTextarea,
} from '@/design-system/components'
import { Deal } from '@/_models'
import Review from '@/components/Review.vue'
import { requests } from '@/_helpers'
import BreezeModal from '@/components/BreezeModal.vue'
import { formatDate } from '@/_helpers/util'
import { event } from 'vue-gtag'
import Multiselect from 'vue-multiselect'

export default {
  components: {
    BreezeModal,
    DsCard,
    DsSelect,
    DsSpinner,
    DsSwitch,
    DsTextarea,
    Multiselect,
    Review,
  },
  data() {
    return {
      reviews: [],
      total: 0,
      from: new Date(),
      until: new Date(),
      sortOrder: 'latest',
      // Display only: the picker still emits a Date.
      dateLabelFormat: { year: 'numeric', month: 'short', day: 'numeric' },
      sortOptions: [
        { value: 'latest', text: 'Latest' },
        { value: 'critical', text: 'Most critical' },
        { value: 'favourable', text: 'Most favourable' },
      ],
      reply: {
        open: false,
        message: '',
        review: null,
      },
      exception: {
        open: false,
        message: '',
        review: {},
      },
      possibleTags: [],
      filteredTags: [],
      withReview: true,
      loading: true,
      refetching: false,
      // Bumped per fetch so a superseded response can't overwrite a newer one.
      fetchGeneration: 0,
    }
  },
  computed: {
    ...mapGetters({
      location: 'authentication/location',
    }),
  },
  watch: {
    // Navbar's location switcher commits to the store without navigating, so without this
    // the previous venue's reviews stayed on screen after a switch. Object syntax because
    // object-shorthand rejects a shorthand method on a string-literal key.
    'location.id': {
      handler(id) {
        if (!id) return
        // Cleared first, so another venue's reviews can't sit dimmed under the spinner
        // while the new ones load.
        this.reviews = []
        this.total = 0
        this.loadReviews()
      },
    },
  },
  created() {
    this.from.setMonth(this.from.getMonth() - 1)
    this.loadReviews()
  },
  methods: {
    dealList(deal) {
      return [new Deal(deal.id)]
    },
    getVisibleDeals(deals) {
      return deals.filter(deal => deal.deprecatedAt == null && deal.dealType.active)
    },
    loadReviews() {
      // Nothing to fetch for a backwards range, and correcting a date calls straight back
      // in through @input, so just drop the spinner.
      if (this.from > this.until) {
        this.loading = false
        this.refetching = false
        return undefined
      }
      // The router guard resolves the partner and enforces `requires: ['location']` before
      // this page is created, so this is belt-and-braces, matching Calendar.
      if (this.location == null) {
        this.loading = false
        return undefined
      }
      if (this.reviews.length > 0) this.refetching = true
      else this.loading = true
      // Two quick filter changes race, and the slower response used to win whichever
      // order they landed in. Same generation trick as the store's hydration.
      this.fetchGeneration += 1
      const generation = this.fetchGeneration
      const superseded = () => generation !== this.fetchGeneration
      const tags = this.filteredTags.map(tag => tag.value)
      return this.location.getReviews(this.from, this.until, this.sortOrder, tags, this.withReview).then(reviews => {
        if (superseded()) return
        this.reviews = reviews.data.list ?? []
        this.total = reviews.data.total ?? 0
        this.possibleTags = (reviews.data.tags ?? []).map(tag => ({
          value: tag.id,
          label: `${tag.name} (${tag.useCount})`,
        }))
      }).catch(() => {
        if (superseded()) return
        this.$notify.error('We couldn’t load your reviews. Please try again.')
      }).finally(() => {
        // A newer request is still running: it owns the spinner now.
        if (superseded()) return
        this.loading = false
        this.refetching = false
      })
    },
    onWithReviewChange(checked) {
      this.withReview = checked
      this.loadReviews()
    },
    startReply(review) {
      this.reply.review = review
      this.reply.open = true
      this.reply.message = null
    },
    startException(review) {
      this.exception.review = review
      this.exception.open = true
      this.exception.message = null
    },
    sendReply() {
      if ((this.reply.message?.length ?? 0) < 20) {
        this.$notify.error('Please enter a message of at least 20 characters')
        return undefined
      }
      // Confirm only once the reply is actually stored, so a failure can't look like a send.
      const { feedbackId } = this.reply.review
      const message = this.reply.message
      return requests.put(`feedbacks/${feedbackId}/reply`, { message }).then(() => {
        this.$notify.success('Reply sent', { title: 'Sent' })
        this.reply.open = false
        const target = this.reviews.find(review => review.feedbackId === feedbackId)
        if (target) {
          this.$set(target, 'replyMessage', message)
          this.$set(target, 'replyAt', new Date())
        }
        event('send-reply')
      }).catch(() => {
        this.$notify.error('We couldn’t send your reply. Please try again.')
      })
    },
    sendException() {
      if ((this.exception.message?.length ?? 0) < 10) {
        this.$notify.error('Please enter a message of at least 10 characters')
        return undefined
      }
      // The review is only dropped from the list once the request lands.
      const { feedbackId } = this.exception.review
      return requests.put(`feedbacks/${feedbackId}/exception`, { message: this.exception.message }).then(() => {
        this.$notify.success('We received your message', { title: 'Sent' })
        this.exception.open = false
        this.reviews = this.reviews.filter(review => feedbackId !== review.feedbackId)
        event('send-exception')
      }).catch(() => {
        this.$notify.error('We couldn’t send your message. Please try again.')
      })
    },
    formatDate(format, date) {
      return formatDate(format, date)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/design-system/mixins';

.rv {
  max-width: 1140px;
  margin: 0 auto;
  color: $c-text;
}

.rv-h1 {
  @include ds-page-title;
}

.rv-lead {
  @include ds-lead;

  strong { color: $c-text; }
}

.rv-stack {
  @include ds-stack;
}

.rv-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 14px 16px;
}

.rv-filter {
  flex: 0 0 auto;
  min-width: 0;
  width: 190px;

  &.wide { width: 260px; }

  &.switch {
    display: flex;
    align-items: center;
    gap: 10px;
    width: auto;
    height: $ds-control-h;
    align-self: flex-end;
  }
}

.rv-filter-label {
  display: block;
  margin: 0 0 5px;
  font-size: 12px;
  font-weight: 600;
  color: $c-text-sec;

  .rv-filter.switch & { margin: 0; }
}

.rv-filter ::v-deep .b-form-datepicker {
  @include ds-datepicker;
}

.rv-filter ::v-deep .ds-select {
  height: $ds-control-h;
}

// vue-multiselect ships its own stylesheet; bring the parts we show into line.
.rv-filter ::v-deep {
  .multiselect,
  .multiselect__tags {
    min-height: $ds-control-h;
  }

  .multiselect__tags {
    @include ds-field;

    height: $ds-control-h;
    padding-top: 10px;
  }

  .multiselect__placeholder,
  .multiselect__single {
    margin: 0;
    padding: 0;
    font-size: 13.5px;
    color: $c-text-ter;
    background: transparent;
  }

  .multiselect__single { color: $c-text; }

  .multiselect__content-wrapper {
    border: 1px solid $c-border;
    border-radius: 10px;
    box-shadow: 0 12px 30px rgba($c-wine, 0.12);
  }

  .multiselect__option--highlight {
    background: $c-wine-light;
    color: $c-wine;

    &::after { display: none; }
  }

  .multiselect__option--selected {
    background: $c-orange-light;
    color: $c-orange;
    font-weight: 600;

    &::after { display: none; }
  }
}

.rv-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 0;
  font-size: 13px;
  color: $c-text-sec;
}

.rv-summary {
  margin: 0;
  font-size: 12.5px;
  color: $c-text-sec;
}

.rv-summary-spinner {
  margin-right: 6px;
}

.rv-list {
  @include ds-stack(10px);

  margin-top: 16px;

  // Refetching: the list stays put so the card keeps its height, but it's clearly not
  // the answer to the filter just changed.
  &.refetching {
    opacity: 0.5;
    pointer-events: none;
  }
}

.rv-modal-text {
  font-size: 13.5px;
  line-height: 1.6;
  color: $c-text-sec;
  margin: 0 0 12px;
}

</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
